import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const TERM_MODE_KEY = 'cliniq_term_filter_mode';   // 'active' | 'historical'
const TERM_ID_KEY   = 'cliniq_term_filter_term_id'; // level.id when historical

const AcademicYearContext = createContext(null);
const STORAGE_KEY = 'cliniq_global_academic_year_id';

function normalizeAcademicYearId(value) {
  return value && value !== 'all' ? String(value) : '';
}

function normalizeAcademicYearLabel(label, startDate) {
  if (startDate) {
    return String(startDate).slice(0, 4);
  }

  const match = String(label || '').match(/(19|20)\d{2}/);
  return match ? match[0] : String(label || '');
}

export function AcademicYearProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [academicYears, setAcademicYears] = useState([]);
  const [globalAcademicYearId, setGlobalAcademicYearIdState] = useState(() => normalizeAcademicYearId(localStorage.getItem(STORAGE_KEY)));
  const [loading, setLoading] = useState(false);

  // ── Term filter state ─────────────────────────────────────────────────────
  // Mode is pinned to 'active': the navbar Archive toggle was removed, so a
  // stored 'historical' mode would strand cascade filters with no UI to reset.
  const [termFilterMode, setTermFilterModeState] = useState('active');
  const [selectedTermId, setSelectedTermIdState] = useState(null);
  const [termLevelFilter, setTermLevelFilter] = useState(''); // e.g. "1st Year MBBS"
  const [activeTerms, setActiveTerms]    = useState([]);
  const [historicalTerms, setHistoricalTerms] = useState([]);
  const [loadingTerms, setLoadingTerms]  = useState(false);

  // ── Global metadata cache (fetched once per login) ──────────────────────────
  const [levels, setLevels] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allBatches, setAllBatches] = useState([]);
  const [allTimeSlots, setAllTimeSlots] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(false);
  // Level-mappings cache: { [levelId]: { departments, categories, timeslots } }
  const levelMappingsCacheRef = useRef({});

  const setGlobalAcademicYearId = useCallback((value) => {
    const normalized = normalizeAcademicYearId(value);
    setGlobalAcademicYearIdState(normalized);
    if (normalized) {
      localStorage.setItem(STORAGE_KEY, normalized);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const setTermFilterMode = useCallback((mode) => {
    setTermFilterModeState(mode);
    localStorage.setItem(TERM_MODE_KEY, mode);
    if (mode === 'active') {
      setSelectedTermIdState(null);
      localStorage.removeItem(TERM_ID_KEY);
    }
  }, []);

  const setSelectedTermId = useCallback((id) => {
    setSelectedTermIdState(id);
    if (id != null) {
      localStorage.setItem(TERM_ID_KEY, String(id));
    } else {
      localStorage.removeItem(TERM_ID_KEY);
    }
  }, []);

  // ── Load active + historical terms ───────────────────────────────────────────
  const loadTerms = useCallback(async () => {
    setLoadingTerms(true);
    try {
      const [activeRes, historicalRes, defaultRes] = await Promise.allSettled([
        api.get('/academic-terms/active'),
        api.get('/academic-terms/historical'),
        api.get('/academic-terms/default-filter'),
      ]);

      const active = activeRes.status === 'fulfilled'
        ? (activeRes.value.data?.terms || []) : [];
      const historical = historicalRes.status === 'fulfilled'
        ? (historicalRes.value.data?.terms || []) : [];

      setActiveTerms(active);
      setHistoricalTerms(historical);

      // Auto-set globalAcademicYearId from default-filter when active mode
      if (defaultRes.status === 'fulfilled' && defaultRes.value.data?.academic_year_id) {
        const detectedYearId = String(defaultRes.value.data.academic_year_id);
        const storedId = normalizeAcademicYearId(localStorage.getItem(STORAGE_KEY));
        // Only override if nothing is stored yet (first login or cleared storage)
        if (!storedId) {
          setGlobalAcademicYearId(detectedYearId);
        }
      }
    } catch (err) {
      // non-fatal — system may not have terms with dates yet
    } finally {
      setLoadingTerms(false);
    }
  }, [setGlobalAcademicYearId]);

  // ── Track whether session-planning meta has been loaded this session ──────────
  const sessionMetaLoadedRef = useRef(false);
  // State mirror of the ref so consumers can defer validation until batches/
  // categories/timeslots actually exist (the ref alone doesn't trigger renders)
  const [sessionMetaLoaded, setSessionMetaLoaded] = useState(false);

  // ── Core metadata: levels + departments — loaded once on login ───────────────
  const loadMetadata = useCallback(async () => {
    setLoadingMeta(true);
    try {
      const [levelsRes, depsRes] = await Promise.all([
        api.get('/academic-levels'),
        api.get('/departments'),
      ]);
      setLevels(levelsRes.data || []);
      setAllDepartments(depsRes.data || []);
      levelMappingsCacheRef.current = {};
    } catch (err) {
      console.error('Failed to load metadata', err);
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  // ── Session-planning metadata: categories, batches, timeslots ────────────────
  // Only loaded when a page that needs them calls ensureSessionMeta().
  const ensureSessionMeta = useCallback(async () => {
    if (sessionMetaLoadedRef.current) return;
    sessionMetaLoadedRef.current = true;
    try {
      const [catsRes, batchesRes, slotsRes] = await Promise.all([
        api.get('/categories'),
        api.get('/batches'),
        api.get('/time-slots'),
      ]);
      setAllCategories(catsRes.data || []);
      setAllBatches(batchesRes.data || []);
      setAllTimeSlots(slotsRes.data || []);
      setSessionMetaLoaded(true);
    } catch (err) {
      console.error('Failed to load session planning metadata', err);
      sessionMetaLoadedRef.current = false; // allow retry on error
    }
  }, []);

  // ── Level mappings: cache-first fetch ────────────────────────────────────────
  const getLevelMappings = useCallback(async (levelId) => {
    if (!levelId) return { departments: [], categories: [], timeslots: [] };
    const cached = levelMappingsCacheRef.current[levelId];
    if (cached) return cached;
    try {
      const { data } = await api.get(`/level-mappings/${levelId}`);
      const result = {
        departments: data.departments || [],
        categories: data.categories || [],
        timeslots: data.timeslots || [],
      };
      levelMappingsCacheRef.current[levelId] = result;
      return result;
    } catch (err) {
      console.error('Failed to load level mappings for', levelId, err);
      return { departments: [], categories: [], timeslots: [] };
    }
  }, []);

  const refreshAcademicYears = useCallback(async () => {
    if (!user) {
      setAcademicYears([]);
      setGlobalAcademicYearIdState('');
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get('/academic-years');
      setAcademicYears(
        data.map((year) => ({
          ...year,
          label: normalizeAcademicYearLabel(year.label, year.start_date),
        })),
      );

      const storedYearId = normalizeAcademicYearId(localStorage.getItem(STORAGE_KEY));
      const preferredYearId = normalizeAcademicYearId(globalAcademicYearId) || storedYearId;
      const preferredYear = preferredYearId
        ? data.find((year) => String(year.id) === String(preferredYearId))
        : null;
      const fallbackYear = data.find((year) => year.is_active) || data[0] || null;
      const resolvedYearId = preferredYear ? String(preferredYear.id) : (fallbackYear ? String(fallbackYear.id) : '');

      setGlobalAcademicYearId(resolvedYearId);
    } catch (error) {
      console.error('Failed to load academic years', error);
    } finally {
      setLoading(false);
    }
  }, [globalAcademicYearId, setGlobalAcademicYearId, user]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    refreshAcademicYears();
    if (user) {
      loadMetadata();
      loadTerms();
    } else {
      // Clear all metadata on logout
      setLevels([]);
      setAllDepartments([]);
      setAllCategories([]);
      setAllBatches([]);
      setAllTimeSlots([]);
      setActiveTerms([]);
      setHistoricalTerms([]);
      levelMappingsCacheRef.current = {};
      sessionMetaLoadedRef.current = false;
      setSessionMetaLoaded(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const selectedAcademicYear = useMemo(
    () => academicYears.find((year) => String(year.id) === String(globalAcademicYearId)) || null,
    [academicYears, globalAcademicYearId],
  );

  return (
    <AcademicYearContext.Provider
      value={{
        // ── Legacy year filter (backward-compatible, used by all existing pages) ──
        academicYears,
        globalAcademicYearId,
        selectedAcademicYear,
        loading,
        refreshAcademicYears,
        setGlobalAcademicYearId,
        // ── Term filter (new framework) ──────────────────────────────────────────
        termFilterMode,
        setTermFilterMode,
        termLevelFilter,
        setTermLevelFilter,
        selectedTermId,
        setSelectedTermId,
        activeTerms,
        historicalTerms,
        loadingTerms,
        refreshTerms: loadTerms,
        // ── Metadata (fetched once, cached globally) ─────────────────────────────
        levels,
        allDepartments,
        allCategories,
        allBatches,
        allTimeSlots,
        loadingMeta,
        sessionMetaLoaded,
        getLevelMappings,
        ensureSessionMeta,
        refreshMeta: async () => { await loadMetadata(); sessionMetaLoadedRef.current = false; await ensureSessionMeta(); },
      }}
    >
      {children}
    </AcademicYearContext.Provider>
  );
}

export function useGlobalAcademicYear() {
  const context = useContext(AcademicYearContext);
  if (!context) {
    throw new Error('useGlobalAcademicYear must be used within AcademicYearProvider');
  }
  return context;
}
